/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useRef, useState } from "react";
import { toast } from "../../../components/ui/sonner";
import { Paperclip, X, Loader2, Film, File as FileIcon } from "lucide-react";
import { authStore } from "../../../lib/api";
import { uploadChunked, uploadErrText, isCanceled } from "../../../lib/upload";
import { T } from "../../../lib/i18n";
import FileUploadDate from "./FileUploadDate";

/* ============================================================================
 * 需求：设计项目「暂停 / 取消申请」需要凭证附件。
 * 背景：设计部经理申请暂停项目时，需提供凭证（业主资金证明 / 市政政策文件 /
 *       与业主的沟通记录等），随申请一并提交给销售总监 / CEO 审批；审批人需能
 *       查看并下载这些凭证，作为批准与否的依据。
 * 职责：本文件只负责「凭证附件」的上传与展示两块 UI，并导出预览 / 下载地址
 *       与体积格式化等工具，供暂停弹窗（PauseCancelDialog）和审批页
 *       （DesignV3Approvals）复用，避免同一套逻辑写两遍。
 * 存储：复用全站通用分片上传（lib/upload.js → POST /uploads/chunk + /uploads/finalize）
 *       拿到 fileId；下载走 GET /files/{fid}/download。后端在审批单里保存
 *       attachments 字段（见 backend/design_v3.py 的 _request_pause_cancel）。
 * ========================================================================== */

const BASE = process.env.REACT_APP_BACKEND_URL;
const tok = () => authStore.get("ali_token") || "";

/* 文件访问地址：后端 /files/{fid}/download 支持 ?auth=<token> 直接访问（新标签打开 / <img> 加载） */
export const fileUrl = (fid) => `${BASE}/api/files/${fid}/download?auth=${tok()}`;
export const thumbUrl = (fid) => `${BASE}/api/files/${fid}/thumb?auth=${tok()}`;
/* 体积格式化：>1MB 显示 MB，否则显示 KB（与评论附件展示保持一致的口径） */
export const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const extOf = (n = "") => (n.split(".").pop() || "").toUpperCase();

/* 附件类型判定：与后端 design_v3.py 的 _file_kind_of 保持同一口径（photo / video / pdf / file）。
   注意：通用分片上传 /uploads/finalize 返回的 kind 是 image / video / pdf / file（用 image 表示图片），
   而评论附件与审批单统一用 photo 表示图片；这里按 photo 口径归一，
   否则上传后弹窗内的图片会因 kind 不匹配而显示成文件图标而非缩略图。 */
const kindOf = (name = "", ct = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if ((ct || "").startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif", "heic"].includes(ext)) return "photo";
  if ((ct || "").startsWith("video/") || ["mp4", "mov", "webm", "m4v"].includes(ext)) return "video";
  if (ct === "application/pdf" || ext === "pdf") return "pdf";
  return "file";
};

/**
 * 单个附件缩略图。
 * 图片 / PDF 直接用缩略图预览（PDF 走 /thumb 首页图），视频 / 其他类型显示图标 + 扩展名。
 * 点击缩略图在新标签打开原文件（可查看 / 下载）；传入 onRemove 时右上角出现删除按钮（仅上传阶段可删）。
 */
export const AttachThumb = ({ f, onRemove, busy, tid }) => (
  <div className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-50" title={`${f.name} · ${fmtSize(f.size)}`} data-testid={tid} data-kind={f.kind}>
    <a href={fileUrl(f.fileId)} target="_blank" rel="noreferrer" className="block h-full w-full">
      {f.kind === "photo" ? <img src={fileUrl(f.fileId)} alt="" className="h-full w-full object-cover" />
        : f.kind === "pdf" ? <img src={thumbUrl(f.fileId)} alt="" className="h-full w-full object-cover bg-white" />
          : <span className="h-full w-full flex flex-col items-center justify-center gap-0.5 text-slate-500">{f.kind === "video" ? <Film className="h-5 w-5 text-sky-600" /> : <FileIcon className="h-5 w-5" />}<span className="text-[9px] font-bold">{extOf(f.name)}</span></span>}
    </a>
    {onRemove && <button type="button" disabled={busy} onClick={onRemove} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute top-0.5 right-0.5 rounded-full bg-black/60 text-white p-0.5 hover:bg-red-600" data-testid={`${tid}-rm`}><X className="h-3 w-3" /></button>}
  </div>
);

/**
 * 附件网格：审批页只读展示（不传 onRemove）与弹窗可删除（传 onRemove）共用。
 * files 元素形如 { fileId, name, size, kind, contentType }。
 */
export const AttachGrid = ({ files = [], onRemove, busy, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid} data-count={files.length}>
    {files.map((f, i) => (
      <div key={f.fileId || i} className="w-40"><AttachThumb f={f} busy={busy} onRemove={onRemove ? () => onRemove(i) : undefined} tid={`${testid}-item-${i}`} /><FileUploadDate value={f.uploadedAt} className="block mt-0.5" /></div>
    ))}
  </div>
);

/**
 * 上传选择器：选择文件 → 通用分片上传（带进度）→ 成功后加入 files 列表；支持逐个删除。
 * 属性：
 *   api       —— 请求实例（useApp 提供）
 *   files     —— 已上传成功的附件数组（受控）
 *   setFiles  —— 更新函数
 *   busy      —— 提交中禁用交互
 *   testid    —— 测试用 data-testid 前缀
 * 说明：串行逐个上传，任一文件失败只提示、不影响已成功的文件；同一文件可重复选择（input 每次重置）。
 */
export const AttachPicker = ({ api, files, setFiles, busy, testid }) => {
  const inp = useRef(null);
  const [up, setUp] = useState(null);   // 当前上传进度 { name, pct }
  const pick = async (e) => {
    const fs = [...(e.target.files || [])];
    e.target.value = "";                 // 重置 value，允许再次选择同一个文件
    if (!fs.length) return;
    for (const f of fs) {
      try {
        setUp({ name: f.name, pct: 0 });
        const fin = await uploadChunked(api, f, { onProgress: ({ pct }) => setUp({ name: f.name, pct }) });
        setFiles((v) => [...v, { fileId: fin.fileId, name: fin.filename || f.name, size: fin.size ?? f.size, contentType: fin.contentType || f.type, kind: kindOf(fin.filename || f.name, fin.contentType || f.type) }]);
      } catch (err) {
        if (!isCanceled(err)) toast.error(uploadErrText(err));
      }
    }
    setUp(null);
  };
  return (
    <div className="rounded-lg border border-slate-200 p-2.5 space-y-2" data-testid={testid}>
      <input ref={inp} type="file" multiple className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" disabled={busy || !!up} onClick={() => inp.current?.click()} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid={`${testid}-pick`}><Paperclip className="h-3.5 w-3.5" />{T("添加附件")}</button>
      {up && <p className="text-[11px] text-amber-700 flex items-center gap-1" data-testid={`${testid}-progress`} data-pct={up.pct}><Loader2 className="h-3 w-3 animate-spin" />{T("上传中")} {up.name} · {up.pct}%</p>}
      {files.length > 0 && <AttachGrid files={files} busy={busy} onRemove={(i) => setFiles((v) => v.filter((_, j) => j !== i))} testid={`${testid}-files`} />}
    </div>
  );
};
